import ClientUserError from "@ea/shared_types/ea.errors";
import type { EndpointDef } from "@ea/shared_types/next/endpoints/core";
import qs from "qs";
import { getApiContext, getApiContextSafe, IS_SERVER_CONTEXT } from "./apiContext";
import { ErrorType, isUserError, unpackError } from "./helpers/error";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyEndpoint = EndpointDef<any, any, any, any>;
type IsAny<T> = 0 extends 1 & T ? true : false;
type IsExactlyVoid<T> = IsAny<T> extends true ? false : T extends void ? true : false;

export const withClientParams =
  <Extra>() =>
  <T extends AnyEndpoint>(endpoint: T) =>
    endpoint as unknown as T & { requestType: T["requestType"] & Extra };

export interface CreateOptions<RequestType> {
  public: boolean;
  fileRequest: boolean;
  withFileName?: boolean;
  useContentType: boolean;
  contentType: string;
  bodyBuilder?: (params: RequestType) => BodyInit | undefined;
  pathParams?: (params: RequestType) => Record<string, string | number>;
  pathCreator?: (path: string, params: RequestType & { id?: number }) => string;
  transformParams: (params: RequestType) => RequestType;
  next?: {
    revalidate?: number | false;
    tags?: string[];
  };
  rawResponseText?: boolean;
  throwOnError?: boolean;
  postQueryParams?: (params: RequestType) => Record<string, string | number>;
}

const defaultCreateOptions = {
  public: false,
  fileRequest: false,
  useContentType: true,
  contentType: "application/json",
  // eslint-disable-next-line
  transformParams: (params: any) => params,
  rawResponseText: false,
};

const buildQuery = <T>(parameters: T) => {
  // EA-504 Json.parse(Json.stringify) is used because qs cannot handle Dayjs object properly
  return qs.stringify(JSON.parse(JSON.stringify(parameters)));
};

// Overloads to force param as null when requestType is void
export function createRequest<
  T extends AnyEndpoint & { requestType: void },
  ResponseType extends T["responseType"] = T["responseType"],
>(
  endpoint: IsExactlyVoid<T["requestType"]> extends true ? T : never,
  options?: Partial<CreateOptions<T["requestType"]>>,
): (
  params: null,
  options?: Pick<CreateOptions<T["requestType"]>, "next" | "pathParams" | "throwOnError"> & {
    token?: string;
    baseUrl?: string;
  },
) => Promise<ResponseType & { error?: ErrorType }>;

export function createRequest<
  T extends AnyEndpoint,
  RequestType extends T["requestType"],
  ResponseType extends T["responseType"],
>(
  endpoint: T,
  options?: Partial<CreateOptions<RequestType>>,
): (
  params: RequestType,
  options?: Pick<CreateOptions<RequestType>, "next" | "pathParams" | "throwOnError"> & {
    token?: string;
    baseUrl?: string;
  },
) => Promise<ResponseType & { error?: ErrorType }>;

export function createRequest<
  T extends AnyEndpoint,
  RequestType extends T["requestType"],
  ResponseType extends T["responseType"],
>(endpoint: T, options?: Partial<CreateOptions<RequestType>>) {
  if (!endpoint) {
    throw new Error("Endpoint not found in ENDPOINTS");
  }
  const method = endpoint.method as string;

  const requestOptions: CreateOptions<RequestType> = {
    ...defaultCreateOptions,
    ...options,
  };

  const prepareEndpoint = (params: RequestType, options?: Partial<CreateOptions<RequestType>>) => {
    if (requestOptions?.pathCreator) {
      return requestOptions.pathCreator(endpoint.path, params);
    } else {
      const pathRequestParams = requestOptions.pathParams
        ? requestOptions.pathParams(params)
        : params;
      const pathParams = options?.pathParams ? options.pathParams(params) : pathRequestParams;
      return Object.entries(pathParams).reduce(
        (path, [key, value]) => path.replaceAll(`:${key}`, `${value}`),
        endpoint.path,
      );
    }
  };

  const request = async (
    params: RequestType,
    options?: Pick<CreateOptions<RequestType>, "next" | "pathParams" | "throwOnError"> & {
      token?: string;
      baseUrl?: string;
    },
  ): Promise<ResponseType & { error?: ErrorType }> => {
    const finalParams = params
      ? requestOptions.transformParams(params as T["requestType"])
      : ({} as T["requestType"]);
    const headers = new Headers();

    let authToken: string;
    let baseUrl: string;

    if (options?.token && options?.baseUrl) {
      authToken = options.token;
      baseUrl = options.baseUrl;
    } else {
      const { getAuthToken, baseUrl: baseUrlFromContext } = getApiContext();
      authToken = (options?.token || (await getAuthToken()))!;
      baseUrl = baseUrlFromContext;
    }

    if (requestOptions.useContentType) {
      headers.set("Content-Type", requestOptions.contentType);
    }

    if (!requestOptions.public) {
      if (!authToken) throw new ClientUserError("Authorization token required");
      headers.set("Authorization", authToken);
    }

    if (requestOptions.fileRequest) {
      headers.set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
    }

    const url = `${baseUrl}/${prepareEndpoint(finalParams, options)}`;
    const fetchOptions: RequestInit & { next: unknown } = {
      method,
      headers,
      next: { revalidate: 0, ...requestOptions.next, ...options?.next },
    };

    if (method === "GET") {
      const queryParams = Object.keys(finalParams).length > 0 ? `?${buildQuery(finalParams)}` : "";

      const response = await fetch(`${url}${queryParams}`, fetchOptions);
      return handleResponse(response, requestOptions);
    } else {
      fetchOptions.body = requestOptions.bodyBuilder
        ? requestOptions.bodyBuilder(finalParams)
        : requestOptions.fileRequest
          ? qs.stringify(finalParams)
          : JSON.stringify(finalParams);

      const response = await fetch(url, fetchOptions);
      return await handleResponse(response, { ...requestOptions, ...options });
    }
  };

  const getServerError = async (response: Response) => {
    try {
      const json = await response.json();
      return json;
    } catch (err) {
      return "Server error";
    }
  };

  const handleResponse = async (response: Response, options: CreateOptions<RequestType>) => {
    if (response.status < 400) {
      try {
        if (requestOptions.fileRequest) {
          if (requestOptions.withFileName) {
            const contentDisposition = response.headers.get("Content-Disposition");
            let filename = "unknown";
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
              if (filenameMatch) {
                filename = filenameMatch[1]!;
              }
            }
            return response.blob().then((blob) => ({ blob, filename }));
          }
          return await response.blob();
        }
        const responseTest = await response.text();
        if (!responseTest) return null;

        if (options.rawResponseText) return responseTest;

        return JSON.parse(responseTest);
      } catch (error) {
        const message = error instanceof Error ? error.message : "An unexpected error occurred";
        throw new Error(`Response parse error: ${message}`);
      }
    }

    if (response.status === 403) {
      if (options.throwOnError) throw new Error("Unauthorized");
      const apiContext = getApiContextSafe();
      return await apiContext?.signOut();
    }
    if (response.status === 503) {
      throw new Error("Connection error");
    } else if (response.status >= 400) {
      const userErrorStr = await getServerError(response);
      const error = unpackError(userErrorStr);

      if (IS_SERVER_CONTEXT || error.code === "INVALID_TOKEN") {
        console.error(error);
        return { error };
      }
      throw isUserError(error) ? new ClientUserError(error) : new Error(error.message);
    }
  };

  return request;
}
